import { useCallback, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FeedType } from '~/constants/lists';

export type PinnableFeedType = Exclude<FeedType, 'search'>;

const STORAGE_KEY = '@default_feed';
const DEFAULT_FEED: PinnableFeedType = 'global';

const validFeeds: PinnableFeedType[] = ['global', 'local', 'network'];

type Listener = (feed: PinnableFeedType) => void;
const listeners = new Set<Listener>();

function notifyListeners(feed: PinnableFeedType) {
  listeners.forEach((fn) => fn(feed));
}

export function useDefaultFeed() {
  const [defaultFeed, setDefaultFeedState] = useState<PinnableFeedType>(DEFAULT_FEED);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const stored = await AsyncStorage.getItem(STORAGE_KEY);
        if (stored && validFeeds.includes(stored as PinnableFeedType)) {
          setDefaultFeedState(stored as PinnableFeedType);
        }
      } catch (e) {
        console.error('Failed to load default feed preference', e);
      } finally {
        setIsLoaded(true);
      }
    })();
  }, []);

  // Subscribe to cross-instance updates
  useEffect(() => {
    const listener: Listener = (feed) => setDefaultFeedState(feed);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  const setDefaultFeed = useCallback(async (feed: PinnableFeedType) => {
    setDefaultFeedState(feed);
    notifyListeners(feed);
    try {
      await AsyncStorage.setItem(STORAGE_KEY, feed);
    } catch (e) {
      console.error('Failed to save default feed preference', e);
    }
  }, []);

  const isDefaultFeed = useCallback(
    (feed: FeedType) => defaultFeed === feed,
    [defaultFeed]
  );

  return { defaultFeed, setDefaultFeed, isDefaultFeed, isLoaded };
}
